import React from 'react';

import {Anchor, Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import type {IconName} from '@fortawesome/fontawesome-common-types';
import classnames from 'classnames';

const VIDEO_EMBED_URL = 'https://www.youtube.com/embed/6u82kKUcXsU?si=U7N5x4kt7QWg2OKc';
const HELP_CENTER_URL = 'https://help.albert.io/en/collections/521216-teachers';
const TEACHER_TALK_URL = 'https://app.lemcal.com/@albert-adoption-team/teacher-talk';

interface HelpLink {
  label: string;
  href: string;
  icon: IconProp;
  chipClassName: string;
}

const HELP_LINKS: HelpLink[] = [
  {
    label: 'Book a free 15-min personalized training session.',
    href: TEACHER_TALK_URL,
    icon: 'circle-user',
    chipClassName: 'bg-[var(--color-data-blue-100)] text-[var(--color-data-blue-700)]'
  },
  {
    label: 'Set up a classroom',
    href: 'https://help.albert.io/en/articles/1884980-teacher-onboarding-getting-started',
    icon: 'chalkboard',
    chipClassName: 'bg-[var(--color-data-green-100)] text-[var(--color-data-green-700)]'
  },
  {
    label: 'View class & student data',
    href: 'https://help.albert.io/en/articles/6683815-how-do-i-use-reports-for-data-analysis-as-a-teacher',
    icon: 'chart-line',
    chipClassName: 'bg-[var(--color-data-orange-100)] text-[var(--color-data-orange-700)]'
  },
  {
    label: 'Assign student practice',
    href: 'https://help.albert.io/en/articles/4942861-how-can-i-assign-practice-on-albert',
    icon: 'file-lines',
    chipClassName: 'bg-[var(--color-data-purple-100)] text-[var(--color-data-purple-700)]'
  },
  {
    label: 'Find questions to assign',
    href: 'https://help.albert.io/en/articles/1885026-how-do-i-search-for-questions-on-albert',
    icon: 'magnifying-glass',
    chipClassName: 'bg-[var(--color-data-red-100)] text-[var(--color-data-red-700)]'
  }
];

interface Props {
  onDismiss: () => void;
  /** Whether the rail is open. Drives the slide/collapse animation — the panel stays mounted. */
  open: boolean;
  /**
   * Narrow screens can't fit the rail beside the content, so it renders as a right-aligned panel over a
   * dimming bg covering the content pane.
   */
  overlay?: boolean;
}

export const GetStartedModule = ({onDismiss, open, overlay}: Props) => {
  const transition = 'transition-all duration-[250ms] ease-out';
  // Shared panel shell; the two breakpoints differ only in positioning/animation.
  const asideBase =
    'flex h-full w-[420px] flex-col gap-x3 overflow-y-auto border-l border-border-primary background-primary p-x4';

  // The panel stays mounted so it can animate closed; mark it inert while closed so its links and
  // close button stay out of the tab order and off the a11y tree. Set via ref because `inert` isn't
  // in this React version's JSX attribute types.
  const setInert = (el: HTMLElement | null) => {
    if (el) {
      el.inert = !open;
    }
  };

  const content = (
    <>
      <div className='flex items-center gap-x1'>
        <Icon
          className='content-primary'
          icon={['fak', 'regular-square-list-circle-question' as IconName]}
        />
        <Text className='grow'>Helpful Links</Text>
        <IconButton
          color='secondary'
          icon='times'
          iconStyle='regular'
          label='Close helpful links'
          onClick={onDismiss}
          tooltip='Close'
          variant='text'
        />
      </div>

      <iframe
        allowFullScreen
        className='aspect-[16/9] w-full rounded-x1 border-none'
        src={VIDEO_EMBED_URL}
        title='Getting started with Albert'
      />

      <ul className='m-x0 flex list-none flex-col gap-x1-half p-x0'>
        {HELP_LINKS.map((link) => (
          <li className='flex' key={link.href}>
            <Anchor
              as='a'
              className='flex grow items-center gap-x1-half rounded-x1-half border border-border-subtle p-x1-half content-primary hover:background-primary-hover active:background-primary-pressed'
              href={link.href}
              noUnderline
              rel='noopener noreferrer'
              target='_blank'
            >
              <span
                className={`flex h-x4 w-x4 shrink-0 items-center justify-center rounded-full text-m ${link.chipClassName}`}
              >
                <Icon color='inherit' icon={link.icon} iconStyle='regular' size='inherit' />
              </span>
              <Text className='grow' size='s'>
                {link.label}
              </Text>
              <Icon
                className='shrink-0 text-xs content-tertiary'
                icon='arrow-up-right'
                iconStyle='regular'
                size='inherit'
              />
            </Anchor>
          </li>
        ))}
      </ul>

      <Text className='text-center' color='secondary' size='s'>
        Find more in our{' '}
        <Anchor
          as='a'
          href={HELP_CENTER_URL}
          inheritColor
          rel='noopener noreferrer'
          target='_blank'
          underlined
        >
          Help Center
        </Anchor>
      </Text>
    </>
  );

  // Narrow screens: a right-aligned panel that slides in over a dimming bg covering the content pane.
  // Clicking the bg — or Enter/Space/Escape when it's focused — dismisses, same as the X.
  if (overlay) {
    return (
      <>
        <div
          aria-label='Close helpful links'
          className={classnames(
            'absolute top-x0 left-x0 z-20 h-full w-full overlay-35',
            transition,
            {
              'cursor-pointer opacity-100': open,
              'pointer-events-none opacity-0': !open
            }
          )}
          onClick={onDismiss}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
              onDismiss();
            }
          }}
          role='button'
          tabIndex={open ? 0 : -1}
        />
        <aside
          aria-label='Getting started'
          className={classnames(
            asideBase,
            'absolute top-x0 right-x0 z-30 max-w-full shadow-l4',
            transition,
            {
              'translate-x-full': !open
            }
          )}
          ref={setInert}
        >
          {content}
        </aside>
      </>
    );
  }

  // Wide screens: the rail sits beside the content. Animate its width so the content reclaims the
  // space smoothly; the inner panel keeps a fixed width and is clipped during the transition.
  return (
    <div
      className={classnames('shrink-0 overflow-hidden', transition, {
        'w-[420px]': open,
        'w-x0': !open
      })}
    >
      <aside aria-label='Getting started' className={asideBase} ref={setInert}>
        {content}
      </aside>
    </div>
  );
};
